<template>
  <div class="card shadow-sm border-0 rounded-4 h-100">
    <div class="card-body p-3">
      <div class="d-flex flex-column justify-content-between align-items-start gap-2 mb-2">
        <h6 class="text-uppercase text-muted m-0">Deuda / Ingreso (DTI)</h6>
        <span class="badge rounded-pill px-3 py-1" :class="badgeClass">{{ label }}</span>
      </div>

      <h5 class="fw-semibold mb-1">{{ currency }} {{ formatNumber(debt) }}</h5>
      <small class="text-muted d-block">Ingreso: {{ currency }} {{ formatNumber(income) }}</small>
      <small class="text-muted d-block">DTI: <strong>{{ dtiPercent }}</strong></small>
      <small class="text-muted">Puntos: <strong>{{ computedPoints.toFixed(1) }}</strong> (0–25)</small>

      <div
        class="progress mt-2"
        style="height:6px"
        role="progressbar"
        aria-label="Puntos por DTI"
        :aria-valuenow="Math.min(100, Math.max(0, (computedPoints/25)*100))"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class="progress-bar" :style="{ width: (computedPoints/25)*100 + '%' }"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DebtCard",
  props: {
    debt:   { type: Number, default: null },
    income: { type: Number, default: null },
    currency: { type: String, default: "Lps" },
    // v-model:points => aporte al score total (0–25)
    points: { type: Number, default: 0 },
    // Límite superior para la última banda (>70%) para mapear hasta 0 pts
    maxDTIRatio: { type: Number, default: 1.5 } // 150%
  },
  emits: ["update:points"],
  computed: {
    ratio() {
      const inc = Number(this.income);
      const d = Number(this.debt);
      if (!Number.isFinite(inc) || inc <= 0 || !Number.isFinite(d) || d < 0) return null;
      return d / inc;
    },
    dtiPercent() {
      if (this.ratio == null) return "—";
      return (this.ratio * 100).toFixed(1) + "%";
    },

    /* RANGOS NUEVOS (0–25 pts):
       < 0.20   → 21–25 (mejor DTI = más puntos)
       0.20–0.35→ 16–20
       0.35–0.50→ 11–15
       0.50–0.70→  6–10
       > 0.70   →  0– 5 (peor DTI = menos puntos)
    */
    computedPoints() {
      const r = this.ratio;
      if (r == null) return 0;

      if (r < 0.20)  return this.mapRange(r, 0.00, 0.20, 25, 21);  // descendente
      if (r <= 0.35) return this.mapRange(r, 0.20, 0.35, 20, 16);
      if (r <= 0.50) return this.mapRange(r, 0.35, 0.50, 15, 11);
      if (r <= 0.70) return this.mapRange(r, 0.50, 0.70, 10, 6);

      const hi = Math.max(this.maxDTIRatio, 0.71);
      return this.mapRange(Math.min(r, hi), 0.70, hi, 5, 0);
    },

    // Etiqueta por puntos según tu tabla
    label() {
      const p = this.computedPoints;
      if (p >= 21) return "Excelente";
      if (p >= 16) return "Bueno";
      if (p >= 11) return "Riesgo Medio";
      if (p >= 6)  return "Riesgo Alto";
      return "Riesgo Muy Alto";
    },
    badgeClass() {
      const p = this.computedPoints;
      if (p >= 16) return "bg-success-subtle text-success-emphasis";
      if (p >= 11) return "bg-warning-subtle text-warning-emphasis";
      if (p >= 6)  return "bg-warning-subtle text-warning-emphasis";
      return "bg-danger-subtle text-danger-emphasis";
    }
  },
  watch: {
    computedPoints: {
      immediate: true,
      handler(val) {
        this.$emit("update:points", Number(val.toFixed(1)));
      }
    }
  },
  methods: {
    formatNumber(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("es-ES", { maximumFractionDigits: 2 });
    },
    // Soporta mapeo ascendente o descendente (out puede bajar o subir)
    mapRange(x, inMin, inMax, outMin, outMax) {
      const clamped = Math.min(Math.max(x, inMin), inMax);
      const ratio = (clamped - inMin) / ((inMax - inMin) || 1);
      return outMin + ratio * (outMax - outMin);
    }
  }
};
</script>

<style scoped>
.bg-success-subtle { background-color: rgba(25,135,84,.12) !important; }
.text-success-emphasis { color: #146c43 !important; }
.bg-warning-subtle { background-color: rgba(255,193,7,.15) !important; }
.text-warning-emphasis { color: #997404 !important; }
.bg-danger-subtle { background-color: rgba(220,53,69,.12) !important; }
.text-danger-emphasis { color: #842029 !important; }
</style>
